import { Constant } from '@app/const';
import { TimeInMs } from '@app/types';
// Dynamic import is used to not bring in heavy dependencies.
type CustomDiagramWidget = import('@app/widgets/CustomDiagramWidget').CustomDiagramWidget;

// Wall-clock budget so 120Hz / background throttling do not shrink or stretch it.
const mountTimeoutMs = 1.5 * TimeInMs.second;

function getCachedCanvas(moduleId: string): CustomDiagramWidget | undefined {
  /** @see CustomDiagramWidget registers itself in the cache in its constructor. */
  return window.apm.cache.get(
    Constant.k.NAMESPACE_DIAGRAM_WIDGET,
    moduleId
  );
}

/**
 * Waits for the canvas of @see moduleId to mount & register itself.
 *
 * @note
 * @see useCanvasTabs.addTab only updates store state, so React has not mounted
 * the new tab's @see CustomDiagramWidget yet & anything posted through
 * @see AppManager.postEventToCurrentView would reach the previously active tab.
 *
 * Resolves immediately when the widget is already cached. Rejects (after
 * `console.error`) once @see mountTimeoutMs passes so a caller can never hang
 * on a silent `null`. A `setTimeout` races `requestAnimationFrame` so a
 * backgrounded tab still times out when frames pause.
 */
export function waitForCanvas(moduleId: string): Promise<CustomDiagramWidget> {
  return new Promise((resolve, reject) => {
    const cachedWidget = getCachedCanvas(moduleId);
    if (cachedWidget) {
      resolve(cachedWidget);
      return;
    }

    const deadline = performance.now() + mountTimeoutMs;
    let frameId = 0;
    let settled = false;
    let timeoutId = 0;

    const finish = (settle: () => void) => {
      if (settled) {
        return;
      }

      settled = true;
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      settle();
    };

    const fail = () => {
      const message = (
        `waitForCanvas: canvas for module '${moduleId}' did not mount within ${mountTimeoutMs}ms.`
      );
      console.error(message);
      finish(() => {
        reject(new Error(message));
      });
    };

    const check = () => {
      const widget = getCachedCanvas(moduleId);
      if (widget) {
        finish(() => {
          resolve(widget);
        });
        return;
      }
      if (performance.now() >= deadline) {
        fail();
        return;
      }

      frameId = requestAnimationFrame(check);
    };

    timeoutId = window.setTimeout(fail, mountTimeoutMs);
    frameId = requestAnimationFrame(check);
  });
}
